/**
 * StepAutoFix.tsx — Optional "Enable Auto-Fix" onboarding step.
 *
 * Shows the least-privilege policy document for each provider, lets the
 * customer copy it, and optionally supply remediation credentials.
 * Skipping is always allowed — credentials can be added later from Settings.
 */

import React, { useState } from 'react';
import { AWS_REGIONS } from '../../../config/awsRegions';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import axios from 'axios';
import API_BASE from '../../../config/api';
import AutoFixConsentModal from '../../../components/AutoFixConsentModal';

// ── Policy documents (inline — matches backend policy_templates.py exactly) ───

const POLICIES: Record<string, string> = {
  aws: `## AWS Least-Privilege Remediation Policy
Create an IAM role named **shield-autofix** and attach this policy:

\`\`\`json
{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "ShieldAutoFixS3",
      "Effect": "Allow",
      "Action": [
        "s3:GetPublicAccessBlock", "s3:PutPublicAccessBlock",
        "s3:GetBucketVersioning", "s3:PutBucketVersioning",
        "s3:GetEncryptionConfiguration", "s3:PutEncryptionConfiguration"
      ],
      "Resource": "arn:aws:s3:::*"
    },
    {
      "Sid": "ShieldAutoFixEC2",
      "Effect": "Allow",
      "Action": ["ec2:DescribeSecurityGroups", "ec2:RevokeSecurityGroupIngress"],
      "Resource": "*"
    },
    {
      "Sid": "ShieldAutoFixIAM",
      "Effect": "Allow",
      "Action": ["iam:GetAccountPasswordPolicy", "iam:UpdateAccountPasswordPolicy"],
      "Resource": "*"
    },
    {
      "Sid": "ShieldAutoFixCloudTrail",
      "Effect": "Allow",
      "Action": ["cloudtrail:DescribeTrails", "cloudtrail:GetTrailStatus", "cloudtrail:StartLogging"],
      "Resource": "*"
    },
    { "Sid": "ShieldIdentity", "Effect": "Allow", "Action": ["sts:GetCallerIdentity"], "Resource": "*" }
  ]
}
\`\`\`

These are the **only** actions Shield needs to apply auto-fixes. No read access to your data, no delete permissions.`,

  gcp: `## GCP Least-Privilege Custom Role
Create a custom role and bind it to a **shield-autofix** service account:

\`\`\`yaml
title: "Shield AutoFix"
description: "Least-privilege role for Shield Auto-Fix"
stage: "GA"
includedPermissions:
  - storage.buckets.getIamPolicy
  - storage.buckets.setIamPolicy
  - compute.firewalls.get
  - compute.firewalls.update
  - resourcemanager.projects.getIamPolicy
  - resourcemanager.projects.setIamPolicy
  - iam.serviceAccountKeys.delete
\`\`\`

\`\`\`bash
gcloud iam roles create ShieldAutoFix --project=<PROJECT> --file=role.yaml
gcloud projects add-iam-policy-binding <PROJECT> \\
  --member="serviceAccount:shield-autofix@<PROJECT>.iam.gserviceaccount.com" \\
  --role="projects/<PROJECT>/roles/ShieldAutoFix"
\`\`\``,

  azure: `## Azure Least-Privilege Custom RBAC Role
Create a custom role named **Shield AutoFix**:

\`\`\`json
{
  "Name": "Shield AutoFix",
  "IsCustom": true,
  "Actions": [
    "Microsoft.Storage/storageAccounts/read",
    "Microsoft.Storage/storageAccounts/write",
    "Microsoft.Network/networkSecurityGroups/securityRules/read",
    "Microsoft.Network/networkSecurityGroups/securityRules/write",
    "Microsoft.KeyVault/vaults/read",
    "Microsoft.KeyVault/vaults/write"
  ],
  "AssignableScopes": ["/subscriptions/<SUBSCRIPTION_ID>"]
}
\`\`\`

\`\`\`bash
az role definition create --role-definition shield-autofix-role.json
az role assignment create --assignee <APP_CLIENT_ID> --role "Shield AutoFix" --scope /subscriptions/<SUB>
\`\`\``,

  ssh: `## SSH Sudoers Configuration
Add to \`/etc/sudoers.d/shield-autofix\` on each managed server:

\`\`\`
# Allow Shield to harden SSH configuration
shield ALL=(ALL) NOPASSWD: /bin/sed -i * /etc/ssh/sshd_config
shield ALL=(ALL) NOPASSWD: /usr/bin/tee -a /etc/ssh/sshd_config
shield ALL=(ALL) NOPASSWD: /bin/systemctl reload sshd
shield ALL=(ALL) NOPASSWD: /usr/sbin/service ssh reload

# Allow Shield to fix file permissions (never deletes)
shield ALL=(ALL) NOPASSWD: /bin/chmod o-w *

# Allow Shield to restrict ports via UFW
shield ALL=(ALL) NOPASSWD: /usr/sbin/ufw deny from any to any port *
shield ALL=(ALL) NOPASSWD: /usr/sbin/ufw allow from 10.0.0.0/8 to any port *
shield ALL=(ALL) NOPASSWD: /usr/sbin/ufw allow from 172.16.0.0/12 to any port *
shield ALL=(ALL) NOPASSWD: /usr/sbin/ufw allow from 192.168.0.0/16 to any port *
shield ALL=(ALL) NOPASSWD: /usr/sbin/ufw --force enable
\`\`\`

\`\`\`bash
chmod 0440 /etc/sudoers.d/shield-autofix && visudo -c
\`\`\``,
};

const PROVIDERS = ['aws', 'gcp', 'azure', 'ssh'] as const;
type Provider = typeof PROVIDERS[number];
const LABELS: Record<Provider, string> = { aws: 'AWS', gcp: 'GCP', azure: 'Azure', ssh: 'SSH' };

interface Props {
  sessionId:  string;
  customerId: string;
  onNext:     () => void;
  onSkip:     () => void;
}

// ── Credential forms (minimal — purpose=remediation) ─────────────────────────

function AWSRemedForm({ sessionId, customerId, onTest }: { sessionId: string; customerId: string; onTest: (c: Record<string, string>) => Promise<void> }) {
  const [step, setStep] = useState<'idle' | 'waiting' | 'done'>('idle');
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [externalId, setExternalId] = useState('');
  const [stackName, setStackName] = useState('');
  const [launchStackUrl, setLaunchStackUrl] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleConsentAccepted() {
    setConsentLoading(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/onboarding/${sessionId}/aws/record-consent`);
      setConsentOpen(false);
      const { data } = await axios.post(`${API_BASE}/onboarding/${sessionId}/aws/init-connection`, {
        purpose: 'remediation',
      });
      setExternalId(data.external_id);
      setStackName(data.stack_name);
      setLaunchStackUrl(data.launch_stack_url || '');
      setStep('waiting');
    } catch (e: any) {
      setError(e.response?.data?.detail ?? 'Failed to initialize.');
    } finally {
      setConsentLoading(false);
    }
  }

  async function handleConfirm() {
    if (!roleArn.startsWith('arn:aws:iam::')) {
      setError('Please enter a valid IAM Role ARN.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/${sessionId}/aws/confirm-connection`, {
        role_arn: roleArn,
        purpose: 'remediation',
      });
      if (data.success) {
        setStep('done');
        await onTest({ _sts_connected: 'true' });
      } else {
        setError(data.message || 'Verification failed.');
      }
    } catch (e: any) {
      setError(e.response?.data?.detail ?? 'Verification failed.');
    } finally {
      setLoading(false);
    }
  }

  if (step === 'idle') {
    return (
      <Stack spacing={2} mt={2}>
        <Button
          variant="contained"
          onClick={() => setConsentOpen(true)}
          sx={{ fontWeight: 700 }}
        >
          Launch Remediation Stack in AWS
        </Button>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        <AutoFixConsentModal
          open={consentOpen}
          loading={consentLoading}
          onAccept={handleConsentAccepted}
          onCancel={() => setConsentOpen(false)}
        />
      </Stack>
    );
  }

  if (step === 'done') {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        Remediation role connected. Auto-Fix is ready for AWS.
      </Alert>
    );
  }

  return (
    <Stack spacing={2} mt={2}>
      <Alert severity="info">
        Deploy the remediation stack in AWS, then paste the Role ARN below.
      </Alert>
      <Box sx={{ bgcolor: 'rgba(0,180,216,0.05)', borderRadius: 2, p: 2 }}>
        <Typography variant="caption" color="text.secondary">Stack Name</Typography>
        <Typography variant="body2" fontFamily="monospace">{stackName}</Typography>
        <Typography variant="caption" color="text.secondary" mt={1} display="block">ExternalId</Typography>
        <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>{externalId}</Typography>
      </Box>
      <Button
        component="a"
        href={launchStackUrl}
        target="_blank"
        rel="noopener noreferrer"
        variant="contained"
        startIcon={<OpenInNewIcon />}
        sx={{ fontWeight: 700 }}
      >
        Launch Stack in AWS Console
      </Button>
      <TextField
        label="Remediation Role ARN"
        fullWidth
        value={roleArn}
        onChange={(e) => setRoleArn(e.target.value)}
        placeholder="arn:aws:iam::123456789012:role/shield-remediation-role"
      />
      <Button variant="contained" disabled={!roleArn || loading} onClick={handleConfirm}>
        {loading ? 'Verifying…' : 'Verify Remediation Role'}
      </Button>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

function GCPRemedForm({ onTest }: { onTest: (c: Record<string, string>) => Promise<void> }) {
  const [pid, setPid] = useState(''); const [kj, setKj] = useState('');
  const ref = React.useRef<HTMLInputElement>(null);
  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = ev => { try { const p = JSON.parse(ev.target?.result as string); setKj(JSON.stringify(p)); if (!pid) setPid(p.project_id || ''); } catch {} };
    r.readAsText(file);
  }
  return (
    <Stack spacing={2} mt={2}>
      <TextField label="Project ID" fullWidth value={pid} onChange={e => setPid(e.target.value)} />
      <input ref={ref} type="file" accept=".json" style={{ display: 'none' }} onChange={handleFile} />
      <Button variant="outlined" fullWidth onClick={() => ref.current?.click()}>{kj ? '✅ JSON key loaded' : 'Upload remediation service account key'}</Button>
      <Button variant="outlined" disabled={!pid || !kj} onClick={() => onTest({ project_id: pid, key_json: kj })}>
        Test & Save Remediation Credentials
      </Button>
    </Stack>
  );
}

function AzureRemedForm({ onTest }: { onTest: (c: Record<string, string>) => Promise<void> }) {
  const [ti, setTi] = useState(''); const [ci, setCi] = useState('');
  const [sec, setSec] = useState(''); const [sub, setSub] = useState('');
  const [show, setShow] = useState(false);
  return (
    <Stack spacing={2} mt={2}>
      <TextField label="Tenant ID" fullWidth value={ti} onChange={e => setTi(e.target.value)} />
      <TextField label="Client ID (remediation app)" fullWidth value={ci} onChange={e => setCi(e.target.value)} />
      <TextField label="Client Secret" fullWidth type={show ? 'text' : 'password'} value={sec} onChange={e => setSec(e.target.value)}
        InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShow(p => !p)}>{show ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
      <TextField label="Subscription ID" fullWidth value={sub} onChange={e => setSub(e.target.value)} />
      <Button variant="outlined" disabled={!ti || !ci || !sec || !sub} onClick={() => onTest({ tenant_id: ti, client_id: ci, client_secret: sec, subscription_id: sub })}>
        Test & Save Remediation Credentials
      </Button>
    </Stack>
  );
}

function SSHRemedForm({ onTest }: { onTest: (c: Record<string, string>) => Promise<void> }) {
  const [host, setHost] = useState(''); const [port, setPort] = useState('22');
  const [user, setUser] = useState(''); const [key, setKey] = useState('');
  const ref = React.useRef<HTMLInputElement>(null);
  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader(); r.onload = ev => setKey(ev.target?.result as string); r.readAsText(file);
  }
  return (
    <Stack spacing={2} mt={2}>
      <TextField label="Server IP / hostname" fullWidth value={host} onChange={e => setHost(e.target.value)} />
      <TextField label="Port" fullWidth type="number" value={port} onChange={e => setPort(e.target.value)} />
      <TextField label="Username" fullWidth value={user} onChange={e => setUser(e.target.value)} />
      <input ref={ref} type="file" accept=".pem,.key" style={{ display: 'none' }} onChange={handleFile} />
      <Button variant="outlined" fullWidth onClick={() => ref.current?.click()}>{key ? '✅ Key loaded' : 'Upload SSH private key'}</Button>
      <Button variant="outlined" disabled={!host || !user || !key} onClick={() => onTest({ host, port, username: user, private_key: key })}>
        Test & Save Remediation Credentials
      </Button>
    </Stack>
  );
}

// ── Copy-policy button ────────────────────────────────────────────────────────

function CopyPolicyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy policy'}>
      <IconButton size="small" onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function StepAutoFix({ sessionId, customerId, onNext, onSkip }: Props) {
  const [tab, setTab]           = useState(0);
  const [testing, setTesting]   = useState(false);
  const [saved, setSaved]       = useState<Record<string, boolean>>({});
  const [error, setError]       = useState<string | null>(null);

  const provider = PROVIDERS[tab];
  const policy   = POLICIES[provider];

  async function handleTest(creds: Record<string, string>) {
    if (creds._sts_connected) {
      setSaved(prev => ({ ...prev, [provider]: true }));
      return;
    }
    setTesting(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/autofix/${customerId}/remediation-credentials`,
        { provider, credentials: creds },
      );
      if (data.success) {
        setSaved(prev => ({ ...prev, [provider]: true }));
      } else {
        setError(data.message || 'Credential test failed.');
      }
    } catch (e: any) {
      setError(e.response?.data?.detail ?? 'Failed to save credentials.');
    } finally {
      setTesting(false);
    }
  }

  const anySaved = Object.values(saved).some(Boolean);

  return (
    <Box maxWidth={720} mx="auto">
      <Box textAlign="center" mb={3}>
        <AutoFixHighIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography variant="h5" fontWeight={700}>Enable Auto-Fix  <Chip label="Optional" size="small" color="info" variant="outlined" /></Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Auto-Fix needs a separate <strong>write-only</strong> role to apply remediations.
          Copy the policy below, apply it to a dedicated identity, then paste the credentials.
          You can always skip this and enable it later from <strong>Settings</strong>.
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {PROVIDERS.map((p) => (
          <Tab key={p} label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {LABELS[p]}
              {saved[p] && <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />}
            </Box>
          } />
        ))}
      </Tabs>

      {/* Policy document */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'rgba(0,0,0,0.15)', position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CopyPolicyBtn text={policy} />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mb={1} fontWeight={600}>
          LEAST-PRIVILEGE POLICY — apply this before entering credentials
        </Typography>
        <Typography
          component="pre"
          sx={{ fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', m: 0 }}
        >
          {policy}
        </Typography>
      </Paper>

      {/* Credential form */}
      {testing ? (
        <Box textAlign="center" py={3}><CircularProgress size={28} /><Typography variant="body2" color="text.secondary" mt={1}>Testing credentials…</Typography></Box>
      ) : (
        <>
          {tab === 0 && <AWSRemedForm sessionId={sessionId} customerId={customerId} onTest={handleTest} />}
          {tab === 1 && <GCPRemedForm   onTest={handleTest} />}
          {tab === 2 && <AzureRemedForm onTest={handleTest} />}
          {tab === 3 && <SSHRemedForm   onTest={handleTest} />}
        </>
      )}

      {saved[provider] && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Remediation credentials saved for {LABELS[provider]}. Auto-Fix is now enabled for this provider.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="text"
          color="inherit"
          startIcon={<SkipNextIcon />}
          onClick={onSkip}
        >
          Skip — I'll do this later in Settings
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!anySaved}
          onClick={onNext}
          startIcon={<AutoFixHighIcon />}
        >
          Continue with Auto-Fix enabled
        </Button>
      </Box>
    </Box>
  );
}
